import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { quotesApi, formatApiError } from '../lib/api';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, LogOut, ShoppingCart, Clock, CheckCircle, XCircle, ArrowRight, Package, Hash, FolderPlus, Folder, Trash2, Plus, GripVertical, FileText, StickyNote, X, Copy, Check, History, ChevronDown, ChevronUp, Calendar, MapPin, UserCog, Archive } from 'lucide-react';
import ProfilePanel from '../components/ProfilePanel';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const statusConfig = (t) => ({
  pending: { label: t('account.status_pending'), color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  confirmed: { label: t('account.status_confirmed'), color: 'bg-green-100 text-green-800', icon: CheckCircle },
  completed: { label: t('account.status_completed'), color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  cancelled: { label: t('account.status_cancelled'), color: 'bg-red-100 text-red-800', icon: XCircle },
  legacy: { label: 'Historique WP', color: 'bg-amber-100 text-amber-800 border border-amber-200', icon: Archive },
});

const AccountPage = () => {
  const { t } = useTranslation();
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [cpuGenerating, setCpuGenerating] = useState(null);

  // Projects
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [editingProject, setEditingProject] = useState(null);
  const [dragItem, setDragItem] = useState(null);

  // CPU History
  const [cpuHistory, setCpuHistory] = useState([]);
  const [cpuLoading, setCpuLoading] = useState(false);
  const [copiedCpu, setCopiedCpu] = useState(null);

  useEffect(() => {
    if (!user && !authLoading) {
      navigate('/connexion?redirect=/compte');
      return;
    }
    const fetchQuotes = async () => {
      try {
        const response = await quotesApi.getQuotes();
        setQuotes(response.data.quotes || []);
      } catch (error) {
        console.error('Failed to fetch quotes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotes();
  }, [user, navigate]);

  const fetchProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/projects/`, { credentials: 'include' });
      const data = await res.json();
      setProjects(data.projects || []);
    } catch (e) {
      console.error('Failed to fetch projects:', e);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && (activeTab === 'projects' || activeTab === 'orders')) fetchProjects();
  }, [user, activeTab, fetchProjects]);

  const fetchCpuHistory = useCallback(async () => {
    setCpuLoading(true);
    try {
      const res = await fetch(`${API_BASE}/cart/cpu-history`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCpuHistory(data.cpus || []);
      }
    } catch (e) {
      console.error('Failed to fetch CPU history:', e);
    } finally {
      setCpuLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && activeTab === 'cpuHistory') fetchCpuHistory();
  }, [user, activeTab, fetchCpuHistory]);

  const createProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/projects/`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newProjectName.trim() })
      });
      if (res.ok) {
        setNewProjectName('');
        fetchProjects();
      }
    } catch (e) { console.error(e); }
  };

  const updateProject = async (projectId, data) => {
    try {
      await fetch(`${API_BASE}/projects/${projectId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      fetchProjects();
    } catch (e) { console.error(e); }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    try {
      await fetch(`${API_BASE}/projects/${projectId}`, { method: 'DELETE', credentials: 'include' });
      fetchProjects();
    } catch (e) { console.error(e); }
  };

  const addItemToProject = async (projectId, type, id) => {
    try {
      await fetch(`${API_BASE}/projects/${projectId}/items`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, id })
      });
      fetchProjects();
    } catch (e) { console.error(e); }
  };

  const removeItemFromProject = async (projectId, type, id) => {
    try {
      await fetch(`${API_BASE}/projects/${projectId}/items`, {
        method: 'DELETE', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, id })
      });
      fetchProjects();
    } catch (e) { console.error(e); }
  };

  const handleDrop = (projectId) => {
    if (!dragItem) return;
    addItemToProject(projectId, dragItem.type, dragItem.id);
    setDragItem(null);
  };

  const generateCpuFromQuote = async (quoteId, items) => {
    setCpuGenerating(quoteId);
    try {
      const res = await fetch(`${API_BASE}/cart/cpu`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items.map(i => ({ product_id: i.product_id || i.id, quantity: i.quantity || 1 })) })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.code) {
          navigator.clipboard.writeText(data.code);
          setCopiedCpu(data.code);
          setTimeout(() => setCopiedCpu(null), 3000);
        }
      }
    } catch (e) { console.error(e); }
    finally { setCpuGenerating(null); }
  };

  const assignQuoteToProject = async (quoteId, projectId) => {
    if (!projectId || projectId === 'none') {
      // Remove from all projects
      for (const p of projects) {
        if (p.quote_ids?.includes(quoteId)) {
          await removeItemFromProject(p.id, 'quote', quoteId);
        }
      }
      return;
    }
    await addItemToProject(projectId, 'quote', quoteId);
  };

  const getQuoteProject = (quoteId) => {
    return projects.find(p => p.quote_ids?.includes(quoteId));
  };

  const handleLogout = () => { logout(); navigate('/'); };
  
  // Wait for auth to load before rendering
  if (authLoading) {
    return (
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-5xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 w-1/3" />
            <div className="h-32 bg-gray-200" />
          </div>
        </div>
      </div>
    );
  }
  
  if (!user) return null;
  const statuses = statusConfig(t);

  const tabs = [
    { id: 'orders', label: t('account.tab_orders'), icon: Package },
    { id: 'projects', label: t('account.tab_projects'), icon: Folder },
    { id: 'cpuHistory', label: t('account.tab_cpu_history'), icon: History },
    { id: 'profile', label: 'Mon profil', icon: UserCog },
  ];

  return (
    <div className="min-h-screen py-8" data-testid="account-page">
      <SEO title="Mon compte" description="Gérez votre compte Puzzle Video." noindex />
      <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-5xl">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">{t('account.subtitle')}</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{t('account.title')}</h1>
        </div>

        {/* User info */}
        <Card className="border border-border rounded-none mb-8" data-testid="user-info-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-surface flex items-center justify-center">
                  <User className="w-6 h-6 text-muted-foreground" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg">{user.name}</h2>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                  {user.company && <p className="text-sm text-muted-foreground">{t('account.company_label')} {user.company}</p>}
                </div>
              </div>
              <Button variant="outline" className="rounded-none" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />{t('account.logout')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-border">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        {/* ── TAB: ORDERS ── */}
        {activeTab === 'orders' && (
          <div>
            {loading ? (
              <div className="space-y-4">{[...Array(3)].map((_, i) => (<div key={i} className="animate-pulse"><div className="h-24 bg-gray-200" /></div>))}</div>
            ) : quotes.length === 0 ? (
              <Card className="border border-border rounded-none" data-testid="no-quotes">
                <CardContent className="py-16 text-center">
                  <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t('account.no_requests')}</h3>
                  <p className="text-muted-foreground mb-6">{t('account.no_requests_desc')}</p>
                  <Link to="/catalogue"><Button className="rounded-none" data-testid="browse-catalogue-btn">{t('account.browse_catalogue')}<ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="quotes-list">
                {quotes.map((quote) => {
                  const status = statuses[quote.status] || statuses.pending;
                  const StatusIcon = status.icon;
                  const isExpanded = expandedQuote === quote.id;
                  const quoteProject = getQuoteProject(quote.id);
                  const isLegacy = !!quote.is_legacy;
                  return (
                    <Card key={quote.id} className={`border rounded-none ${isLegacy ? 'border-amber-200 bg-amber-50/30' : 'border-border'}`} data-testid={`quote-${quote.id}`}
                      draggable={!isLegacy} onDragStart={() => !isLegacy && setDragItem({ type: 'quote', id: quote.id })} onDragEnd={() => setDragItem(null)}
                    >
                      <CardContent className="p-0">
                        {/* Quote header — clickable */}
                        <button
                          className="w-full p-6 text-left hover:bg-gray-50/60 transition-colors"
                          onClick={() => setExpandedQuote(isExpanded ? null : quote.id)}
                          data-testid={`quote-toggle-${quote.id}`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {!isLegacy && <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />}
                              {isLegacy && <Archive className="w-4 h-4 text-amber-600" />}
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-semibold">
                                    {isLegacy ? `Commande WP #${quote.wp_order_id}` : `${t('account.request_number')} #${quote.id?.slice(-6)}`}
                                  </h3>
                                  <Badge className={`rounded-none text-[10px] uppercase tracking-wider ${status.color}`} data-testid={`quote-badge-${quote.id}`}>
                                    <StatusIcon className="w-3 h-3 mr-1 inline" />{status.label}
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {t('account.created_on')} {new Date(quote.created_at).toLocaleDateString('fr-FR')} {t('account.at')} {new Date(quote.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                                {quote.cpu_code && <p className="text-xs font-mono text-primary mt-0.5">CUP: {quote.cpu_code}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                            </div>
                          </div>
                          <Separator className="my-3" />
                          <div className="flex items-center justify-between text-sm">
                            <div className="text-muted-foreground">
                              {quote.items?.length || 0} {t('account.articles_count')}
                              {quote.items?.length > 0 && (<><span className="mx-1">—</span><span>{quote.items[0].name}</span>{quote.items.length > 1 && (<span className="text-muted-foreground"> +{quote.items.length - 1} {t('account.others')}</span>)}</>)}
                            </div>
                            {quote.total > 0 && <span className="font-semibold">{quote.total.toFixed(2)} EUR {t('account.ht')}</span>}
                          </div>
                        </button>

                        {/* Expanded order details */}
                        {isExpanded && (
                          <div className="border-t border-border px-6 pb-6 pt-4 space-y-4 bg-gray-50/50" data-testid={`quote-details-${quote.id}`}>
                            {/* Dates & plages */}
                            {(quote.pickup_date || quote.return_date) && (
                              <div className="flex flex-wrap gap-4 text-sm">
                                {quote.pickup_date && (
                                  <div className="flex items-center gap-1.5">
                                    <Calendar className="w-3.5 h-3.5 text-primary" />
                                    <span className="font-medium">Enlèvement:</span>
                                    <span>{new Date(quote.pickup_date).toLocaleDateString('fr-FR')}</span>
                                    {quote.pickup_slot && <Badge variant="outline" className="rounded-none text-[10px] h-5">{quote.pickup_slot}</Badge>}
                                  </div>
                                )}
                                {quote.return_date && (
                                  <div className="flex items-center gap-1.5">
                                    <Calendar className="w-3.5 h-3.5 text-orange-500" />
                                    <span className="font-medium">Retour:</span>
                                    <span>{new Date(quote.return_date).toLocaleDateString('fr-FR')}</span>
                                    {quote.return_slot && <Badge variant="outline" className="rounded-none text-[10px] h-5">{quote.return_slot}</Badge>}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Date d'envoi */}
                            <div className="text-xs text-muted-foreground">
                              Envoyé le {new Date(quote.created_at).toLocaleDateString('fr-FR')} à {new Date(quote.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </div>

                            {/* Products list */}
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Produits</p>
                              <div className="space-y-1.5">
                                {(quote.items || []).map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-sm bg-white border border-border px-3 py-2">
                                    <div className="flex items-center gap-3">
                                      {item.image_url && <img src={item.image_url} alt="" className="w-8 h-8 object-cover" />}
                                      <div>
                                        <span className="font-medium">{item.name}</span>
                                        {item.ref && <span className="text-xs text-muted-foreground ml-2">(ref: {item.ref})</span>}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-right">
                                      <span className="text-xs text-muted-foreground">x{item.quantity || 1}</span>
                                      {item.daily_price > 0 && <span className="font-medium">{item.daily_price.toFixed(2)} EUR/j</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Actions: Assign to project + Generate CPU (only for new quotes) */}
                            {!isLegacy && (
                            <div className="flex items-center gap-3 pt-2">
                              {/* Project dropdown */}
                              <div className="flex items-center gap-2">
                                <Folder className="w-4 h-4 text-muted-foreground" />
                                <Select
                                  value={quoteProject?.id || 'none'}
                                  onValueChange={(val) => assignQuoteToProject(quote.id, val)}
                                >
                                  <SelectTrigger className="rounded-none h-8 text-xs w-48" data-testid={`quote-project-select-${quote.id}`}>
                                    <SelectValue placeholder="Assigner à un projet" />
                                  </SelectTrigger>
                                  <SelectContent className="rounded-none">
                                    <SelectItem value="none" className="text-xs">Aucun projet</SelectItem>
                                    {projects.map(p => (
                                      <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              {/* Generate CPU button */}
                              {quote.items?.length > 0 && (
                                <Button
                                  variant="outline" size="sm" className="rounded-none h-8 text-xs gap-1"
                                  onClick={() => generateCpuFromQuote(quote.id, quote.items)}
                                  disabled={cpuGenerating === quote.id}
                                  data-testid={`quote-generate-cpu-${quote.id}`}
                                >
                                  {cpuGenerating === quote.id ? (
                                    <><Clock className="w-3 h-3 animate-spin" /> Génération...</>
                                  ) : copiedCpu && cpuGenerating === null ? (
                                    <><Check className="w-3 h-3 text-green-600" /> CUP copié: {copiedCpu}</>
                                  ) : (
                                    <><Hash className="w-3 h-3" /> Générer un CUP</>
                                  )}
                                </Button>
                              )}
                            </div>
                            )}
                            {isLegacy && (
                              <p className="text-[11px] italic text-amber-700 pt-1">Commande archivée depuis l'ancien site WordPress — lecture seule.</p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: PROJECTS ── */}
        {activeTab === 'projects' && (
          <div className="space-y-6">
            {/* New project */}
            <div className="flex gap-2">
              <Input
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Nom du nouveau projet..."
                className="rounded-none"
                onKeyDown={(e) => e.key === 'Enter' && createProject()}
                data-testid="new-project-input"
              />
              <Button className="rounded-none" onClick={createProject} disabled={!newProjectName.trim()} data-testid="create-project-btn">
                <FolderPlus className="w-4 h-4 mr-2" /> Créer
              </Button>
            </div>

            {projectsLoading ? (
              <div className="space-y-4">{[1,2].map(i => <div key={i} className="animate-pulse h-32 bg-gray-100" />)}</div>
            ) : projects.length === 0 ? (
              <Card className="border border-border rounded-none">
                <CardContent className="py-12 text-center">
                  <Folder className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">Aucun projet</h3>
                  <p className="text-sm text-muted-foreground">Créez un projet pour organiser vos devis, CUP et factures.</p>
                </CardContent>
              </Card>
            ) : (
              projects.map(project => (
                <Card
                  key={project.id}
                  className={`border rounded-none transition-colors ${dragItem ? 'border-primary/50 border-dashed' : 'border-border'}`}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-primary/5'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('bg-primary/5'); }}
                  onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-primary/5'); handleDrop(project.id); }}
                  data-testid={`project-${project.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      {editingProject === project.id ? (
                        <Input
                          defaultValue={project.name}
                          className="rounded-none text-lg font-semibold h-8"
                          onBlur={(e) => { updateProject(project.id, { name: e.target.value }); setEditingProject(null); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { updateProject(project.id, { name: e.target.value }); setEditingProject(null); }}}
                          autoFocus
                          data-testid={`project-name-input-${project.id}`}
                        />
                      ) : (
                        <CardTitle className="text-lg cursor-pointer hover:text-primary transition-colors" onClick={() => setEditingProject(project.id)} data-testid={`project-name-${project.id}`}>
                          <Folder className="w-4 h-4 inline mr-2 text-primary" />{project.name}
                        </CardTitle>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{new Date(project.created_at).toLocaleDateString('fr-FR')}</span>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => deleteProject(project.id)} data-testid={`delete-project-${project.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Notes */}
                    <Textarea
                      defaultValue={project.notes || ''}
                      placeholder="Notes sur ce projet..."
                      className="rounded-none text-sm min-h-[60px] resize-none"
                      onBlur={(e) => { if (e.target.value !== (project.notes || '')) updateProject(project.id, { notes: e.target.value }); }}
                      data-testid={`project-notes-${project.id}`}
                    />

                    {/* CPU codes */}
                    {project.cpu_codes?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1"><Hash className="w-3 h-3" /> Codes Panier (CUP)</p>
                        <div className="flex flex-wrap gap-1.5">
                          {project.cpu_codes.map(code => (
                            <div key={code} className="flex items-center gap-1 bg-primary/10 border border-primary/20 px-2 py-0.5 text-xs font-mono">
                              <Link to={`/panier?cpu=${code}`} className="hover:underline text-primary font-bold">{code}</Link>
                              <button onClick={() => removeItemFromProject(project.id, 'cpu', code)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quote IDs — clickable to expand */}
                    {project.quote_ids?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Devis</p>
                        <div className="flex flex-wrap gap-1.5">
                          {project.quote_ids.map(qid => (
                            <div key={qid} className="flex items-center gap-1 bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs">
                              <button
                                className="hover:underline hover:text-blue-700 font-medium"
                                onClick={() => { setActiveTab('orders'); setExpandedQuote(qid); }}
                                data-testid={`project-quote-link-${qid}`}
                              >
                                #{qid.slice(-6)}
                              </button>
                              <button onClick={() => removeItemFromProject(project.id, 'quote', qid)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Invoice IDs */}
                    {project.invoice_ids?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1"><StickyNote className="w-3 h-3" /> Factures</p>
                        <div className="flex flex-wrap gap-1.5">
                          {project.invoice_ids.map(iid => (
                            <div key={iid} className="flex items-center gap-1 bg-green-50 border border-green-200 px-2 py-0.5 text-xs">
                              <span>#{iid.slice(-6)}</span>
                              <button onClick={() => removeItemFromProject(project.id, 'invoice', iid)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <p className="text-[10px] text-muted-foreground italic">Glissez-déposez des devis depuis l'onglet "Commandes & Devis" vers ce projet.</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* ── TAB: CPU HISTORY ── */}
        {activeTab === 'cpuHistory' && (
          <div>
            <h2 className="text-lg font-semibold mb-4">{t('account.cpu_history_title')}</h2>
            {cpuLoading ? (
              <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="animate-pulse h-16 bg-gray-100" />)}</div>
            ) : cpuHistory.length === 0 ? (
              <Card className="border border-border rounded-none">
                <CardContent className="py-12 text-center">
                  <Hash className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">{t('account.cpu_history_empty')}</h3>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="cpu-history-list">
                {cpuHistory.map((cpu) => (
                  <Card key={cpu.code} className="border border-border rounded-none" data-testid={`cpu-item-${cpu.code}`}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-primary/10 border border-primary/20 px-3 py-1.5">
                          <span className="font-mono font-bold text-primary text-sm tracking-wider">{cpu.code}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium">{cpu.items?.length || 0} {t('account.cpu_history_items')}</p>
                          <p className="text-xs text-muted-foreground">
                            {cpu.created_at ? new Date(cpu.created_at).toLocaleDateString('fr-FR') + ' ' + new Date(cpu.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline" size="sm" className="rounded-none h-8 text-xs gap-1"
                          onClick={() => { navigator.clipboard.writeText(cpu.code); setCopiedCpu(cpu.code); setTimeout(() => setCopiedCpu(null), 2000); }}
                          data-testid={`copy-cpu-${cpu.code}`}
                        >
                          {copiedCpu === cpu.code ? <><Check className="w-3 h-3" /> {t('admin.uploads_copied')}</> : <><Copy className="w-3 h-3" /> {t('account.cpu_copy')}</>}
                        </Button>
                        <Link to={`/panier?cpu=${cpu.code}`}>
                          <Button size="sm" className="rounded-none h-8 text-xs gap-1" data-testid={`load-cpu-${cpu.code}`}>
                            <ShoppingCart className="w-3 h-3" /> {t('account.cpu_load')}
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: PROFILE ── */}
        {activeTab === 'profile' && <ProfilePanel />}
      </div>
    </div>
  );
};

export default AccountPage;
